import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useClientProject } from "../../contexts/ClientProjectContext";
import { toast } from "sonner";
import { Activity, Users } from "lucide-react";
import PieChartCard from "../../components/charts/PieChartCard";
import BarChartCard from "../../components/charts/BarChartCard";
import LineChartCard from "../../components/charts/LineChartCard";

function StatCard({ icon: Icon, label, value, sub, tone = "default" }) {
  const tones = {
    default: "bg-white",
    primary: "bg-gradient-to-br from-indigo-50 to-white border-indigo-200",
    success: "bg-gradient-to-br from-emerald-50 to-white border-emerald-200",
    warn: "bg-gradient-to-br from-amber-50 to-white border-amber-200",
    info: "bg-gradient-to-br from-sky-50 to-white border-sky-200",
  };
  return (
    <div
      className={`glass-panel p-5 ${tones[tone]}`}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon className="h-4 w-4 text-neutral-600" />
      </div>
      <div className="font-display text-3xl tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-neutral-600 mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function Panel({ title, subtitle, children, testid }) {
  return (
    <section className="glass-panel p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="font-display text-lg uppercase tracking-tight">{title}</h3>
          {subtitle && (
            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function ClientDashboard() {
  const { projectId } = useClientProject();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const qs = projectId && projectId !== "__all" ? `?project_id=${projectId}` : "";
        const r = await api.get(`/client/dashboard${qs}`);
        setData(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  const grid = data?.today_stock_grid;
  const active = data?.active_shifts || [];
  const conversion = data?.conversion || { surveys_total: 0, surveys_with_purchase: 0, rate_percent: 0 };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl uppercase tracking-tight">Overview</h1>
        <p className="text-xs font-mono uppercase tracking-widest text-neutral-500 mt-1">
          Project insights · refresh by switching the project above
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <StatCard
          icon={Activity}
          label="Active shifts"
          value={loading ? "—" : active.length}
          sub={active.length === 0 ? "No one checked in" : `${active.length} promoter${active.length === 1 ? "" : "s"} live`}
          tone="primary"
        />
        <StatCard
          icon={Users}
          label="Total surveys"
          value={loading ? "—" : conversion.surveys_total}
          tone="info"
        />
      </div>

      {/* Current product mix — pie with side legend */}
      <PieChartCard
        title="Current product mix"
        subtitle="What customers currently use (all surveys)"
        testid="client-current-product"
        data={data?.by_current_product || []}
        height="h-80 sm:h-[460px]"
        inner={0}
        outer={170}
      />

      {/* Sales by outlet (v2.2) */}
      <LineChartCard
        title="Sales by outlet · total units"
        subtitle="All SKUs · units sold (from surveys)"
        testid="client-sales-by-store-total"
        data={data?.sales_by_store || []}
        height="h-60 sm:h-72"
      />

      <BarChartCard
        title="Sales by SKU · total units"
        subtitle="Units sold per SKU (from surveys, all dates)"
        testid="client-sales-by-sku"
        data={data?.sales_by_sku || []}
        height="h-72 sm:h-96"
        xAxisAngle={-30}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <PieChartCard
          title="Heard about UBBS"
          subtitle="How customers found out"
          testid="client-heard"
          data={data?.by_heard || []}
          height="h-64 sm:h-80"
          inner={60}
          outer={120}
        />
        <PieChartCard
          title="Nicotine level"
          subtitle="Preferred strength"
          testid="client-nicotine"
          data={data?.by_nicotine_level || []}
          height="h-64 sm:h-80"
          inner={0}
          outer={120}
        />
      </div>

      <PieChartCard
        title="Nationality mix"
        subtitle="Customer nationalities surveyed"
        testid="client-nationality-mix"
        data={data?.nationality_mix || []}
        height="h-64 sm:h-80"
        inner={60}
        outer={120}
      />

      {/* Today's stock SKU × Outlet */}
      <Panel
        title="Today's stock · SKU × Outlet"
        subtitle={grid?.date ? `Latest counts · ${grid.date}` : "Latest counts"}
        testid="client-today-stock"
      >
        {!grid || (grid.rows || []).length === 0 || (grid.stores || []).length === 0 ? (
          <div className="h-44 flex items-center justify-center text-sm text-neutral-500 font-mono uppercase tracking-wider">
            No stock recorded today
          </div>
        ) : (
          <div className="max-h-[32rem] overflow-auto glass-panel">
            <table className="text-sm border-separate border-spacing-0 w-full">
              <thead className="sticky top-0 z-20 bg-white/50">
                <tr>
                  <th className="sticky left-0 z-30 bg-white/50 text-left p-2 font-mono text-[10px] uppercase tracking-widest text-neutral-700 border-b border-r border-slate-300/60 min-w-[180px]">
                    SKU
                  </th>
                  {grid.stores.map((st) => (
                    <th key={st} className="text-right p-2 font-mono text-[10px] uppercase tracking-widest text-neutral-700 whitespace-nowrap border-b border-slate-300/60 min-w-[100px]">
                      {st}
                    </th>
                  ))}
                  <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest text-black border-b border-l-2 border-slate-400/60 bg-white/70 min-w-[80px] sticky right-0 z-20">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {grid.rows.map((r, idx) => (
                  <tr key={r.sku} className={`${idx % 2 === 0 ? "bg-white" : "bg-white/40"} hover:bg-blue-50`}>
                    <td className={`sticky left-0 z-10 p-2 font-medium border-b border-r border-slate-200/70 whitespace-nowrap ${idx % 2 === 0 ? "bg-white" : "bg-white/50"}`}>
                      {r.sku}
                    </td>
                    {grid.stores.map((st) => {
                      const v = r[st] ?? 0;
                      return (
                        <td key={st} className={`p-2 text-right font-mono tabular-nums border-b border-slate-200/70 ${v === 0 ? "text-neutral-300" : "text-black"}`}>
                          {v}
                        </td>
                      );
                    })}
                    <td className="sticky right-0 z-10 p-2 text-right font-mono tabular-nums font-bold border-b border-l-2 border-slate-400/60 bg-white/70">
                      {r.total ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 z-20 bg-slate-900 text-white">
                <tr>
                  <td className="sticky left-0 z-30 bg-slate-900 text-white p-2 font-mono text-[10px] uppercase tracking-widest border-r border-white/20">
                    Total
                  </td>
                  {grid.stores.map((st) => (
                    <td key={st} className="p-2 text-right font-mono tabular-nums font-bold whitespace-nowrap">
                      {grid.totals?.[st] ?? 0}
                    </td>
                  ))}
                  <td className="sticky right-0 z-30 bg-slate-900 text-white p-2 text-right font-mono tabular-nums font-bold border-l-2 border-white/40">
                    {grid.totals?.__grand__ ?? 0}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
